import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import FourDigitCode from "./FourDigitCode";
import "./public.css";
import { IoWarning } from "react-icons/io5";
import Project from "../project/Project";

export default function PublicProjectHome() {
	const [searchParams] = useSearchParams();
	const token = searchParams.get("token");
	const [passcode, setPasscode] = useState("");
	const [invalidPass, setInvalidPass] = useState(true);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const { id } = useParams();
	const [authorized, setAuthorized] = useState(false);
	const [tempToken, setTempToken] = useState("");
	const [isApproved, setIsApproved] = useState(false);
	const [isRejected, setIsRejected] = useState(false);
	const [rejectedBy, setRejectedBy] = useState("");
	const [rejectedReason, setRejectedReason] = useState("");
	const [rejectedAt, setRejectedAt] = useState(null);
	const [passcodeRequired, setPasscodeRequired] = useState(null);
	const [shopEnabled, setShopEnabled] = useState(0);
	let api = process.env.REACT_APP_API_URL;

	useEffect(() => {
		const checkPasscodeRequirement = async () => {
			try {
				const response = await fetch(api + "/public/check-for-passcode", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
					},
					body: JSON.stringify({ projectId: id, hash: token }),
				});

				const data = await response.json();

				if (data.err) {
					setError(data.err);
					setPasscodeRequired(true);
				} else {
					setPasscodeRequired(data.passcodeRequired);

					if (!data.passcodeRequired) {
						setAuthorized(true);
						setTempToken(data.tempToken);
						setIsApproved(data.isApproved);
					}
				}
			} catch (err) {
				setError("Failed to check access requirements");
				setPasscodeRequired(true);
			} finally {
				setLoading(false);
			}
		};

		if (id && token) {
			checkPasscodeRequirement();
		}
	}, [id, token, api]);

	useEffect(() => {
		if (authorized && tempToken && id && token) {
			fetch(api + "/public/public-link/" + id, {
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					Authorization: "Bearer " + tempToken,
				},
			})
				.then(res => res.ok ? res.json() : null)
				.then(data => {
					if (data && Array.isArray(data.links)) {
						const current = data.links.find(link => link.hash === token);
						setShopEnabled(current && current.shop_enabled === 1 ? 1 : 0);
						setIsRejected(Boolean(current && (current.is_rejected ?? current.isRejected)));
						setRejectedBy(current?.rejecter_name || current?.rejected_by || current?.rejecter || "");
						setRejectedReason(current?.reject_reason || current?.rejected_reason || current?.reason || "");
						setRejectedAt(current?.rejected_at || current?.rejected_on || current?.rejected_time || null);
					}
				});
		}
	}, [authorized, tempToken, id, token, api]);

	const submitCode = () => {
		setLoading(true);
		fetch(api + "/public/validate", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
			},
			body: JSON.stringify({ projectId: id, hash: token, passcode }),
		})
			.then((res) => {
				return res.json();
			})
			.then((data) => {
				if (data) {
					if (data.err) {
						setError(data.err);
					} else {
						setAuthorized(true);
						setTempToken(data.tempToken);
						setIsApproved(data.isApproved);
					}
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	if (authorized && tempToken) {
		return (
			<div>
				<Project
					isGuest={true}
					guestToken={tempToken}
					isApproved={isApproved}
					isRejected={isRejected}
					rejectedBy={rejectedBy}
					rejectedReason={rejectedReason}
					rejectedAt={rejectedAt}
					setIsApproved={setIsApproved}
					shopEnabled={shopEnabled}
				/>
			</div>
		);
	}

	if (loading) {
		return (
			<div className="h-screen w-full flex justify-center items-center bg-black">
				<div className="flex flex-col ">
					<div className="bg-white p-8  rounded-md shadow-lg">
						<div className="flex gap-2 justify-center items-center mt-4">
							<div className="dot"></div>
							<div className="dot"></div>
							<div className="dot"></div>
						</div>
						<p className="mt-5">{passcodeRequired === null ? "Checking access..." : "Validating..."}</p>
					</div>
				</div>
			</div>
		);
	}

	if (passcodeRequired) {
		return (
			<div className="h-screen w-full flex justify-center items-center bg-black">
				<div className="flex flex-col min-h-[30vh]">
					<div className="bg-white p-8 md:max-w-[30vw] lg:max-w-[20vw] rounded-md shadow-lg">
						<div className="flex justify-center items-center mt-2 mb-10">
							<img src="https://rhino-public-resources.s3.us-west-1.amazonaws.com/logo-line.png" alt="logo" className="h-12 w-auto" />
						</div>
						<p>This is a password protected page.</p>
						<div className="flex items-center gap-2 mb-8">
							<p>Please enter the passcode:</p>
						</div>
						<FourDigitCode setCompletedCode={setPasscode} setIncomplete={setInvalidPass} />
						<button
							className={`${invalidPass ? "bg-gray-400" : "bg-green-500 hover:bg-green-600"} w-full text-white p-2 rounded-md mt-8`}
							disabled={invalidPass}
							onClick={submitCode}>
							Enter Code
						</button>
						{error ? (
							<div className="flex items-center gap-2 mt-2 bg-yellow-300 p-2 rounded-md">
								<IoWarning className="text-3xl" />
								<p>{error}</p>
							</div>
						) : null}
					</div>
				</div>
			</div>
		);
	} else {
		return null;
	}
}
